import type { EcwIdeaReference } from '@citizen-walk/shared-types';
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { db } from '../../lib/firebase';
import { addIdeaToOutbox, type OutboxIdea } from './ecwOutbox';

/**
 * 処方箋カード機能 (Sprint X5c minimum、REQ-4.2.2)。
 *
 * Path: `/districts/{districtId}/ecwIdeas/{ideaId}` (docs/04 §4-2)。
 *
 * minimum 仕様:
 *   - onSnapshot で list subscribe (createdAt desc)
 *   - createIdea: 手動入力 (title / observations / actionIdea / insightIds / selectedPostIds) + serverTimestamp
 *   - deleteIdea: 本人のみ (rules で enforce)
 *   - hidden=true (admin モデレーション) は client 側で filter
 *
 * 旧 IdeaCardList / IdeaForm との差分:
 *   - AI 対話 (Step3-4) は scope 外
 *   - llmActionQuestions / llmEffectQuestions / llmInsightSummary は default 値で書込
 *   - referenceImageBase64 / team / realName は scope 外
 */

export interface IdeaMinimal {
  id: string;
  districtId: string;
  userId: string;
  title: string;
  observations: string;
  actionIdea: string;
  nickname: string;
  insightIds: string[];
  selectedPostIds: string[];
  hidden: boolean;
  createdAt: Timestamp | null;
  /** Sprint AW: AI 提案の参考事例 (画像 + 外部 link、最大 5 件)。 */
  references?: EcwIdeaReference[];
  /* === Phase B+1.4 拡張: 旧 EcwIdea full schema 読み出し対応 (詳細 modal の AI 対話ログ + hero 画像 + filter 用) === */
  /** キャッチ画像 (dataURL or http URL)。旧 IdeaCardList の hero / カード上部。 */
  referenceImageBase64?: string;
  /** AI Step3 出力 (actionQuestions[])。 */
  llmActionQuestions?: string[];
  /** AI Step4 出力 (effectQuestions[])。 */
  llmEffectQuestions?: string[];
  /** back モード起動時に AI 生成された Insight 要約。 */
  llmInsightSummary?: string;
  /** 起動 context (Plutchik 8 感情)。 */
  emotionTag?: string;
  emotionDescription?: string;
  /** 起動 context (categoryMaster コード / 表示名)。 */
  categoryCode?: string;
  categoryLabel?: string;
  /** 参加セッション名 join (旧踏襲)。 */
  team?: string;
  realName?: string;
  /** メインセッション ID (作成時の membership.mainSessionId)。表示はセッション名に解決。 */
  sessionId?: string | null;
}

export type IdeasState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: IdeaMinimal[]; error: null }
  | { status: 'error'; items: []; error: Error };

export interface CreateIdeaInput {
  userId: string;
  nickname: string;
  title: string;
  observations: string;
  actionIdea: string;
  insightIds: string[];
  selectedPostIds: string[];
  /** Sprint AW: AI 提案の参考事例 (optional)。 */
  references?: EcwIdeaReference[];
  /* === Phase B 拡張: 旧 EcwIdea full schema 互換 (全 optional) === */
  /** キャッチ画像 (外部 URL or dataURL、旧 referenceImageBase64)。 */
  referenceImageBase64?: string;
  /** AI Step3 出力 (actionQuestions[])。 */
  llmActionQuestions?: string[];
  /** AI Step4 出力 (effectQuestions[])。 */
  llmEffectQuestions?: string[];
  /** back モード起動時に AI 生成された Insight 要約 (300 字以内)。 */
  llmInsightSummary?: string;
  /** 参加セッション名 join。 */
  team?: string;
  /** 本名 (印刷物 / 表示用、旧踏襲)。 */
  realName?: string;
  /** 起動時 context (Plutchik 8 感情)。 */
  emotionTag?: string;
  /** ECW_EMOTION_DESCRIPTIONS[emotionTag] 解決結果。 */
  emotionDescription?: string;
  /** categoryMaster コード。 */
  categoryCode?: string;
  /** categoryMaster 表示名。 */
  categoryLabel?: string;
  /** メインセッション ID (作成時の membership.mainSessionId、AnalysisPage が注入)。 */
  sessionId?: string | null;
  /**
   * Phase B: 同 title 既存時に `_2`, `_3`, ... を自動付加する場合は呼出側で
   * `ensureUniqueIdeaTitle` を使って解決済 title を渡すこと (hook 内では加工しない、
   * 既存 idea 一覧を hook が知らないため)。
   */
}

/**
 * Firestore に EcwIdea doc を直接書込 (online 時、または outbox flush 時に呼ばれる)。
 *
 * `useEcwIdeas().createIdea` の online ブランチと、useEcwOutbox.flush の send 経路で
 * 共有される実装本体。`addDoc` payload 構築 logic を hook 外に抽出することで、outbox
 * flush が React component lifecycle に依存しなくなる。
 */
export async function sendIdeaToFirestore(
  input: CreateIdeaInput & { districtId: string },
): Promise<string> {
  const col = collection(db, 'districts', input.districtId, 'ecwIdeas');
  const now = serverTimestamp();
  const ref = await addDoc(col, {
    schemaVersion: 1,
    districtId: input.districtId,
    userId: input.userId,
    nickname: input.nickname,
    title: input.title.trim(),
    observations: input.observations.trim(),
    actionIdea: input.actionIdea.trim(),
    insightIds: input.insightIds,
    selectedPostIds: input.selectedPostIds,
    hidden: false,
    createdAt: now,
    updatedAt: now,
    createdBy: input.userId,
    updatedBy: input.userId,
    archivedAt: null,
    ...(input.references && input.references.length > 0 ? { references: input.references } : {}),
    ...(input.referenceImageBase64 ? { referenceImageBase64: input.referenceImageBase64 } : {}),
    ...(input.llmActionQuestions && input.llmActionQuestions.length > 0
      ? { llmActionQuestions: input.llmActionQuestions }
      : {}),
    ...(input.llmEffectQuestions && input.llmEffectQuestions.length > 0
      ? { llmEffectQuestions: input.llmEffectQuestions }
      : {}),
    ...(input.llmInsightSummary ? { llmInsightSummary: input.llmInsightSummary } : {}),
    ...(input.team ? { team: input.team } : {}),
    ...(input.realName ? { realName: input.realName } : {}),
    ...(input.emotionTag ? { emotionTag: input.emotionTag } : {}),
    ...(input.emotionDescription ? { emotionDescription: input.emotionDescription } : {}),
    ...(input.categoryCode ? { categoryCode: input.categoryCode } : {}),
    ...(input.categoryLabel ? { categoryLabel: input.categoryLabel } : {}),
    ...(input.sessionId ? { sessionId: input.sessionId } : {}),
  });
  return ref.id;
}

/**
 * outbox 1 件 (idea) を Firestore に送信 (useEcwOutbox.flush から呼出)。
 * 成功時は新 ideaId を返す (呼出側で deleteIdeaFromOutbox)。
 * 失敗時は例外を throw (呼出側で attempts++ + lastError 記録)。
 */
export async function sendIdeaOutboxItem(item: OutboxIdea): Promise<string> {
  return await sendIdeaToFirestore({
    districtId: item.districtId,
    userId: item.userId,
    nickname: item.nickname,
    title: item.title,
    observations: item.observations,
    actionIdea: item.actionIdea,
    insightIds: item.insightIds,
    selectedPostIds: item.selectedPostIds,
    ...(item.references ? { references: item.references } : {}),
    ...(item.referenceImageBase64 ? { referenceImageBase64: item.referenceImageBase64 } : {}),
    ...(item.llmActionQuestions ? { llmActionQuestions: item.llmActionQuestions } : {}),
    ...(item.llmEffectQuestions ? { llmEffectQuestions: item.llmEffectQuestions } : {}),
    ...(item.llmInsightSummary ? { llmInsightSummary: item.llmInsightSummary } : {}),
    ...(item.emotionTag ? { emotionTag: item.emotionTag } : {}),
    ...(item.emotionDescription ? { emotionDescription: item.emotionDescription } : {}),
    ...(item.categoryCode ? { categoryCode: item.categoryCode } : {}),
    ...(item.categoryLabel ? { categoryLabel: item.categoryLabel } : {}),
    ...(item.team ? { team: item.team } : {}),
    ...(item.realName ? { realName: item.realName } : {}),
    ...(item.sessionId ? { sessionId: item.sessionId } : {}),
  });
}

export function useEcwIdeas(
  // 地区フォールバック根絶: 地区未解決時は null。effect は no-op し、create/delete は呼ばれない。
  districtId: string | null,
  /**
   * 再試行用 key (Sprint AM、UI2 無限スピナー解消)。
   * 呼出側で setRetryKey(k=>k+1) するたびに subscribe を貼り直す。
   */
  retryKey: number = 0,
): {
  state: IdeasState;
  createIdea: (input: CreateIdeaInput) => Promise<string>;
  deleteIdea: (ideaId: string) => Promise<void>;
} {
  const [state, setState] = useState<IdeasState>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    if (!districtId) {
      setState({ status: 'ready', items: [], error: null });
      return;
    }
    // retry 時に必ず loading に戻す
    setState({ status: 'loading', items: [], error: null });
    const col = collection(db, 'districts', districtId, 'ecwIdeas');
    const q = query(col, orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items: IdeaMinimal[] = snap.docs
          .map((d) => {
            const data = d.data();
            // Sprint AW: references を読み出し (旧 doc には無いので fallback undefined)
            const rawRefs = Array.isArray(data.references) ? data.references : undefined;
            const references = rawRefs
              ? rawRefs
                  .map((r): EcwIdeaReference | null => {
                    if (!r || typeof r !== 'object') return null;
                    const rr = r as Record<string, unknown>;
                    const title = typeof rr.title === 'string' ? rr.title : '';
                    if (!title) return null;
                    const out: EcwIdeaReference = { title };
                    if (typeof rr.description === 'string') out.description = rr.description;
                    // Sprint AW reviewer fixup (defense-in-depth、docs/13 §3): Firestore に
                    // 不正データが直書きされても href/src に javascript: 等が渡らないよう、
                    // read-side でも http(s) prefix を検証 (callable 側 sanitize と二重防御)。
                    if (typeof rr.url === 'string' && /^https?:\/\//.test(rr.url)) out.url = rr.url;
                    if (typeof rr.imageUrl === 'string' && /^https?:\/\//.test(rr.imageUrl)) {
                      out.imageUrl = rr.imageUrl;
                    }
                    return out;
                  })
                  .filter((x): x is EcwIdeaReference => x !== null)
              : undefined;
            return {
              id: d.id,
              districtId: (data.districtId as string) ?? districtId,
              userId: (data.userId as string) ?? '',
              title: (data.title as string) ?? '',
              observations: (data.observations as string) ?? '',
              actionIdea: (data.actionIdea as string) ?? '',
              nickname: (data.nickname as string) ?? '',
              insightIds: Array.isArray(data.insightIds) ? (data.insightIds as string[]) : [],
              selectedPostIds: Array.isArray(data.selectedPostIds)
                ? (data.selectedPostIds as string[])
                : [],
              hidden: data.hidden === true,
              createdAt: (data.createdAt as Timestamp | undefined) ?? null,
              ...(references && references.length > 0 ? { references } : {}),
              // Phase B+1.4: AI 対話ログ + hero 画像 + context (旧 EcwIdea full schema)
              ...(typeof data.referenceImageBase64 === 'string'
                ? { referenceImageBase64: data.referenceImageBase64 as string }
                : {}),
              ...(Array.isArray(data.llmActionQuestions)
                ? { llmActionQuestions: data.llmActionQuestions as string[] }
                : {}),
              ...(Array.isArray(data.llmEffectQuestions)
                ? { llmEffectQuestions: data.llmEffectQuestions as string[] }
                : {}),
              ...(typeof data.llmInsightSummary === 'string'
                ? { llmInsightSummary: data.llmInsightSummary as string }
                : {}),
              ...(typeof data.emotionTag === 'string'
                ? { emotionTag: data.emotionTag as string }
                : {}),
              ...(typeof data.emotionDescription === 'string'
                ? { emotionDescription: data.emotionDescription as string }
                : {}),
              ...(typeof data.categoryCode === 'string'
                ? { categoryCode: data.categoryCode as string }
                : {}),
              ...(typeof data.categoryLabel === 'string'
                ? { categoryLabel: data.categoryLabel as string }
                : {}),
              ...(typeof data.team === 'string' ? { team: data.team as string } : {}),
              ...(typeof data.realName === 'string' ? { realName: data.realName as string } : {}),
              sessionId: typeof data.sessionId === 'string' ? data.sessionId : null,
            };
          })
          .filter((it) => !it.hidden);
        setState({ status: 'ready', items, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return unsubscribe;
  }, [districtId, retryKey]);

  const createIdea = useCallback(
    async (input: CreateIdeaInput): Promise<string> => {
      if (!districtId) throw new Error('createIdea: districtId が未指定です (地区解決前)');
      const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
      if (!isOnline) {
        const id = await addIdeaToOutbox({
          createdLocalAt: Date.now(),
          districtId,
          userId: input.userId,
          nickname: input.nickname,
          title: input.title,
          observations: input.observations,
          actionIdea: input.actionIdea,
          insightIds: input.insightIds,
          selectedPostIds: input.selectedPostIds,
          ...(input.references ? { references: input.references } : {}),
          ...(input.referenceImageBase64
            ? { referenceImageBase64: input.referenceImageBase64 }
            : {}),
          ...(input.llmActionQuestions ? { llmActionQuestions: input.llmActionQuestions } : {}),
          ...(input.llmEffectQuestions ? { llmEffectQuestions: input.llmEffectQuestions } : {}),
          ...(input.llmInsightSummary ? { llmInsightSummary: input.llmInsightSummary } : {}),
          ...(input.emotionTag ? { emotionTag: input.emotionTag } : {}),
          ...(input.emotionDescription ? { emotionDescription: input.emotionDescription } : {}),
          ...(input.categoryCode ? { categoryCode: input.categoryCode } : {}),
          ...(input.categoryLabel ? { categoryLabel: input.categoryLabel } : {}),
          ...(input.team ? { team: input.team } : {}),
          ...(input.realName ? { realName: input.realName } : {}),
          ...(input.sessionId ? { sessionId: input.sessionId } : {}),
          lastError: null,
          attempts: 0,
        });
        return `outbox:${id}`;
      }
      return await sendIdeaToFirestore({ ...input, districtId });
    },
    [districtId],
  );

  const deleteIdea = useCallback(
    async (ideaId: string): Promise<void> => {
      if (!districtId) throw new Error('deleteIdea: districtId が未指定です (地区解決前)');
      await deleteDoc(doc(db, 'districts', districtId, 'ecwIdeas', ideaId));
    },
    [districtId],
  );

  return { state, createIdea, deleteIdea };
}
